import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { FLAGS } from '../services/growthbook/flags';
import { GrowthBookService } from '../services/growthbook/growth-book.service';
import { InitialLoadingService } from '../services/initial-loading.service';

/**
 * Interface for configuring the feature flag guard data in route definitions.
 *
 * @interface FeatureFlagGuardData
 * @property {FLAGS[]} requiredFlags - Array of feature flags that must be enabled for route access
 * @property {string} [redirectTo] - Optional redirect path when access is denied (defaults to '/forbidden')
 */
export interface FeatureFlagGuardData {
  requiredFlags: FLAGS[];
  redirectTo?: string;
}

/**
 * Angular route guard that controls access to routes based on feature flags.
 *
 * This guard checks if all required feature flags are enabled using GrowthBook service.
 * If any required flag is disabled, the user is redirected to an forbidden page.
 *
 * @example
 * // Basic usage in route configuration
 * {
 *   path: 'monetization',
 *   component: MonetizationComponent,
 *   canActivate: [featureFlagGuard],
 *   data: {
 *     requiredFlags: [FLAGS.MONETIZATION_ENABLED]
 *   }
 * }
 *
 * @example
 * // Multiple flags required (ALL must be enabled)
 * {
 *   path: 'advanced-analytics',
 *   component: AdvancedAnalyticsComponent,
 *   canActivate: [featureFlagGuard],
 *   data: {
 *     requiredFlags: [FLAGS.ANALYTICS_ENABLED, FLAGS.ADVANCED_FEATURES]
 *   }
 * }
 *
 * @example
 * // Custom redirect path
 * {
 *   path: 'beta-feature',
 *   component: BetaFeatureComponent,
 *   canActivate: [featureFlagGuard],
 *   data: {
 *     requiredFlags: [FLAGS.BETA_FEATURES],
 *     redirectTo: '/coming-soon'
 *   }
 * }
 *
 * @param route - The activated route snapshot containing guard data
 * @param _state - The router state snapshot (unused)
 * @returns {boolean} - True if access is granted, false if redirected
 */
export const featureFlagGuard: CanActivateFn = (route, _state) => {
  const growthBookService = inject(GrowthBookService);
  const router = inject(Router);
  const initialLoadingService = inject(InitialLoadingService);

  return initialLoadingService.waitForInitialization(() => {
    const data = route.data as FeatureFlagGuardData;

    if (!data.requiredFlags || data.requiredFlags.length === 0) {
      return true;
    }

    const hasPermission = data.requiredFlags.every(flag => growthBookService.isFeatureOn(flag));

    if (!hasPermission) {
      const redirectTo = data.redirectTo || '/forbidden';
      router.navigate([redirectTo]);
      return false;
    }

    return true;
  });
};
